/** A row of the Keystone `user_team` table: the directory that maps a team name to its Amazon team ID. */
export interface Team {
  team_id: string;
  team_name: string;
}

/** A Keystone user as returned by GET /users. TEAM users carry the team ID (or the team name when it resolves). */
export interface KeystoneUser {
  type: string;
  value: string;
}

export const TEAM_ID_PREFIX = 'amzn1.abacus.team.';

// Same pattern as the service's is_team_id(), so the UI rejects what the service would mis-handle.
const TEAM_ID_PATTERN = /^amzn[1-9]\.abacus\.team\..+/;

/** Returns an error message naming the failed condition, or null when the team ID is valid. */
export function validateTeamId(teamId: string, teams: Team[], ignore?: Team): string | null {
  const id = teamId.trim();
  if (!id) return 'Team ID is required';
  if (!TEAM_ID_PATTERN.test(id)) return `Team ID must start with ${TEAM_ID_PREFIX}`;
  const clash = teams.find(t => t.team_id === id && t !== ignore);
  if (clash) return `Team ID is already registered as '${clash.team_name}'`;
  return null;
}

/** Returns an error message naming the failed condition, or null when the team name is valid. */
export function validateTeamName(teamName: string, teams: Team[], ignore?: Team): string | null {
  const name = teamName.trim();
  if (!name) return 'Team name is required';
  const clash = teams.find(t => t.team_name.toLowerCase() === name.toLowerCase() && t !== ignore);
  if (clash) return `Team name '${clash.team_name}' is already registered`;
  return null;
}

/** A team is in use when a TEAM user refers to it by ID or by name. */
export function isTeamInUse(team: Team, teamUsers: KeystoneUser[]): boolean {
  return teamUsers.some(u => u.type === 'TEAM' && (u.value === team.team_id || u.value === team.team_name));
}
